import {
  Order,
  MultiOrderCustomsInfoQueryQuery,
  core_apimessages_Money,
} from '@reverbdotcom/commons/src/gql/graphql';
import {
  findCustomsInfoForOrder,
  ICustomsInfo,
} from './customs_info_utils';
import { convertMoney } from '@reverbdotcom/commons/src/convert_currency';
import { parseAmount, formatAmount, formatAmountCents } from '@reverbdotcom/commons/src/money';
import { get } from 'lodash';

type ExchangeRates = MultiOrderCustomsInfoQueryQuery['exchangeRates']['rates'];

export function overCustomsLimit(
  countryCode: string,
  orders: Order[],
  customsInfo: ICustomsInfo[],
  exchangeRates: ExchangeRates,
): boolean {
  const maximumValue = maximumAllowedValue(countryCode);

  if (!maximumValue || !maximumValue.amountCents) {
    return false;
  }

  const declaredValue = declaredValueTotal(orders, customsInfo);

  const maximumValueInDeclaredValueCurrency =
    convertMoney(maximumValue, declaredValue.currency, exchangeRates);

  return declaredValue.amountCents > maximumValueInDeclaredValueCurrency.amountCents;
}

export function formattedDeclaredValueTotal(
  orders: Order[],
  customsInfo: ICustomsInfo[],
): string {
  const { amountCents, currency } = declaredValueTotal(orders, customsInfo);
  return formatAmountCents({ amountCents, currency });
}

export function formattedMaximumAllowedValue(
  countryCode: string,
  toCurrency: string,
  exchangeRates: ExchangeRates,
): string {

  const maximumValue = maximumAllowedValue(countryCode);
  if (!maximumValue || !maximumValue.amountCents) {
    return '';
  }

  const convertedMaximumValue = convertMoney(maximumValue, toCurrency, exchangeRates);

  return formatAmount(convertedMaximumValue);
}

function declaredValueTotal(
  orders: Order[],
  customsInfo: ICustomsInfo[],
): Pick<core_apimessages_Money, 'amountCents' | 'currency'> {
  const customsInfos = orders.map((order) => {
    return findCustomsInfoForOrder(order.id, customsInfo);
  });

  const declaredValues = customsInfos.map((customsInfo) => {
    return customsInfo.declaredValue;
  });

  const declaredAmountCents = declaredValues.map((declaredValue) => {
    return parseAmount(declaredValue.amount, declaredValue.currency).amountCents;
  });

  const { currency } = declaredValues[0];
  const amountCents = declaredAmountCents.reduce((valueA, valueB) => {
    return valueA + valueB;
  });

  return { amountCents, currency };
}

function maximumAllowedValue(countryCode: string): { amountCents: number; currency: string } | undefined {
  return get(MAXIMUM_DECLARED_VALUES, countryCode);
}

const MAXIMUM_DECLARED_VALUES: { [country: string]: { amountCents: number; currency: string } } = {
  CA: { amountCents: 200000, currency: 'CAD' },
  DE: { amountCents: 100000, currency: 'EUR' },
  GR: { amountCents: 80000, currency: 'EUR' },
  PR: { amountCents: 250000, currency: 'USD' },
  US: { amountCents: 250000, currency: 'USD' },
};
